import React, { createContext, useState, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Drawer, useMediaQuery, Box, Stack, Typography, Button, DialogTitle, DialogContent, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { setTemplate } from 'api/users';
import ResumeOnboarded from 'components/ResumeOnBoarded/ResumeOnboarded';

const ONBOARDING_FLAG_KEY = 'shouldShowOnboarding';
const OnboardingContext = createContext();

export const OnboardingProvider = ({ children }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [shouldShowOnboarding, setShouldShowOnboardingState] = useState(
        localStorage.getItem(ONBOARDING_FLAG_KEY) === 'true'
    );
    const [profileRefreshTrigger, setProfileRefreshTrigger] = useState(0);
    const [isChoicePopupOpen, setIsChoicePopupOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewLink, setPreviewLink] = useState(null);
    const [finalOnboardingData, setFinalOnboardingData] = useState(null);
    const [onFlowComplete, setOnFlowComplete] = useState(null);
    const [isLoading, setIsLoading] = useState(false); // For the CircularProgress
    const [replaceTrigger, setReplaceTrigger] = useState(0)

    const fileInputRef = useRef(null);

    /**
     * Sets the onboarding flag to show/hide the onboarding process
     * and updates the state accordingly. The onboarding flag is stored in
     * local storage and is set to 'true' when the onboarding should be shown
     * and removed when it should be hidden.
     *
     * @param {Boolean} shouldShow - If true, the onboarding should be shown.
     * If false, it should be hidden.
     */
    const setShouldShowOnboarding = (shouldShow) => {
        if (shouldShow) {
            localStorage.setItem(ONBOARDING_FLAG_KEY, 'true');
            setIsChoicePopupOpen(true);
        } else {
            localStorage.removeItem(ONBOARDING_FLAG_KEY);
            setIsChoicePopupOpen(false);
        }
        setShouldShowOnboardingState(shouldShow);
    };

/**
 * Initiates the onboarding flow by opening the choice popup and optionally
 * setting a callback function to be executed upon completion of the onboarding.
 * 
 * @param {Object} callbacks - An object containing optional callback functions.
 * @param {Function} [callbacks.onComplete] - A callback function to be called 
 * when the onboarding flow is completed.
 */

    const triggerOnboardingFlow = (callbacks) => {
        if (callbacks?.onComplete) setOnFlowComplete(() => callbacks.onComplete);
        setIsChoicePopupOpen(true);
    };

    /**
     * Handles the 'Replace' button click in the preview. This function:
     * 1. Closes the preview dialog
     * 2. Re-opens the choice/upload dialog
     * 3. Increments a counter to signal to the ResumeOnboarded component that
     *    it should refresh. This is necessary because the component is not
     *    re-rendered when the state changes.
     */
    const handleReplaceClick = () => {
        setIsPreviewOpen(false); // Close the preview
        setIsChoicePopupOpen(true); // Re-open the choice/upload popup
        // Increment the counter to send the signal to ResumeOnboarded
        setReplaceTrigger(prev => prev + 1);
    };

    /**
     * Handles the resume selection during the onboarding process.
     * If the user selects 'using-existing-resume' and provides a valid resume URL,
     * it sets the preview link, updates final onboarding data, hides the onboarding,
     * and opens the preview. Otherwise, it defaults to the 'talent-junction-style'
     * template, attempts to update the user's template preference, hides the onboarding,
     * triggers a profile refresh, and navigates to the profile page. Logs an error if
     * the template update fails.
     *
     * @param {Object} result - The result object containing the user's choice and resume URL.
     * @param {string} result.choice - The user's choice of resume option.
     * @param {string} result.resumeUrl - The URL of the user's existing resume.
     */
    const handleResumeSelection = async (result) => {
        const { choice, resumeUrl } = result;
        setIsChoicePopupOpen(false);

        if (choice === 'using-existing-resume' && resumeUrl) {
            setPreviewLink(resumeUrl);
            setFinalOnboardingData({ choice, resumeUrl });
            setIsPreviewOpen(true);
            setReplaceTrigger(0);
        } else {
            const templateData = 'talent-juntion-style';
            try {
                await setTemplate({ data: templateData });
                setShouldShowOnboarding(false);
                setProfileRefreshTrigger(prev => prev + 1);
                if (onFlowComplete) {
                    onFlowComplete(); // Use callback if available
                } else {
                    navigate('/profile', {
                        state: { chosenAlignment: templateData, newResumeUrl: null }
                    });
                }
            } catch (error) {
                console.error("Failed to set resume template preference", error);
            }
        }
    };

    /**
     * Continues the onboarding process after the user previews the resume.
     * This function retrieves the final onboarding data and sets the template
     * to 'using-existing-resume'. It then hides the preview, triggers a profile
     * refresh, and navigates to the profile page with the selected template
     * alignment and resume URL. Logs an error if setting the template preference fails.
     */
    const handlePreviewContinue = async () => {
        if (!finalOnboardingData) return;

        const { resumeUrl } = finalOnboardingData;
        const templateData = 'using-existing-resume';

        try {
            await setTemplate({ data: templateData });
            setIsPreviewOpen(false);
            setShouldShowOnboarding(false);
            setProfileRefreshTrigger(prev => prev + 1);
            if (onFlowComplete) {
                onFlowComplete();
            } else {
                navigate('/profile', {
                    state: {
                        chosenAlignment: templateData,
                        newResumeUrl: resumeUrl
                    }
                });
            }
        } catch (error) {
            console.error("Failed to set resume template preference", error);
        }
    };

    const contextValue = {
        shouldShowOnboarding,
        setShouldShowOnboarding,
        profileRefreshTrigger,
        triggerOnboardingFlow
    };

    return (
        <OnboardingContext.Provider value={contextValue}>
            {children}
            {isMobile ? (
                <Drawer
                    anchor="bottom"
                    open={isChoicePopupOpen}
                    onClose={(event, reason) => {
                        if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
                            return;
                        }
                    }}
                    className="resume-onboard-screen mobile-drawer-view"
                >
                    <ResumeOnboarded onClose={handleResumeSelection} triggerFilePicker={replaceTrigger} />
                </Drawer>
            ) : (
                <Dialog
                    open={isChoicePopupOpen}
                    onClose={(event, reason) => {
                        if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
                            return;
                        }
                    }}
                    className="forgot-password-dialog"
                >
                    <ResumeOnboarded onClose={handleResumeSelection} triggerFilePicker={replaceTrigger} />
                </Dialog>
            )}

            {isMobile ? (
                <Drawer open={isPreviewOpen} anchor={'right'} fullScreen className='preview-resume-dialog' sx={{ "& .MuiDrawer-paper": { width: "100%" }, zIndex: '1300!important' }}>
                    <Box sx={{ width: '100%' }} className="job-app-drawer-container" role="presentation">
                        <Box className="drawer-header">
                            <Stack direction="row" alignItems={'flex-start'} spacing={1}>
                                <Box>
                                    <Typography className="title">Uploaded Resume</Typography>
                                </Box>
                            </Stack>
                        </Box>
                        <Box className="drawer-content" padding={'0px !important'}>
                            <Box className="my-resume-overview" >
                                {isLoading ? (
                                    <Stack justifyContent="center" alignItems="center" >
                                        <CircularProgress size={40} />
                                    </Stack>
                                ) : previewLink ? (
                                    <>
                                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js" />
                                        <div style={{ width: '100%' }}>
                                            <Viewer fileUrl={previewLink}
                                                defaultScale={.628}
                                                style={{ width: '100%' }} />
                                        </div>
                                    </>
                                ) : (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            height: "100%",
                                            width: "100%",
                                            border: "2px dashed #ccc",
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                            textAlign: "center",
                                            padding: "20px"
                                        }}
                                        onClick={() => fileInputRef.current.click()}
                                    >
                                        <Typography variant="body1" color="textSecondary">No resume uploaded</Typography>
                                        <Button
                                            variant="contained"
                                            sx={{ mt: 2, backgroundColor: "#1575C6", "&:hover": { backgroundColor: "#0056b3" } }}
                                        >
                                            Please upload in PDF (preferred) or word format
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        <Box className="drawer-footer">
                            <Stack direction="row" spacing={2}>
                                <Button fullWidth size="large" className="cancel-btn"
                                    onClick={handleReplaceClick}
                                    disabled={isLoading}>
                                    Replace
                                </Button>
                                <Button fullWidth variant="contained" size="large" className="update-btn"
                                    onClick={handlePreviewContinue}
                                    disabled={isLoading}>
                                    Continue
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                </Drawer>
            ) : (
                <Dialog open={isPreviewOpen}
                    sx={{ maxWidth: "600px", margin: "auto", zIndex: 1300 }} fullWidth>
                    <DialogTitle sx={{ padding: "12px 20px", borderBottom: "1px solid #ddd" }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>Uploaded Resume</Typography>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={handleReplaceClick}
                                    disabled={isLoading}
                                    sx={{ color: "#1575C6", textTransform: "none", fontWeight: 500 }}
                                >
                                    Replace
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handlePreviewContinue}
                                    disabled={isLoading}
                                    sx={{
                                        backgroundColor: "#1575C6",
                                        color: "white",
                                        textTransform: "none",
                                        fontWeight: 500,
                                        "&:hover": { backgroundColor: "#0056b3" },
                                    }}
                                >
                                    Continue
                                </Button>
                            </Stack>
                        </Stack>
                    </DialogTitle>

                    <DialogContent sx={{ padding: "0 !important", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Box
                            className="resume-options-preview"
                            sx={{
                                height: "75vh",
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                borderRadius: "8px",
                            }}
                        >
                            {isLoading ? (
                                <Stack justifyContent="center" alignItems="center">
                                    <CircularProgress size={40} />
                                </Stack>
                            ) : previewLink ? (
                                <>
                                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js" />
                                    <Box sx={{ width: "100%", height: "100%" }}>
                                        <div style={{ width: '100%' }}>
                                            <Viewer fileUrl={previewLink}
                                                defaultScale={.874}
                                                style={{ width: '100%' }} />
                                        </div>
                                    </Box>
                                </>
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        height: "100%",
                                        width: "100%",
                                        border: "2px dashed #ccc",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        textAlign: "center",
                                        padding: "20px"
                                    }}
                                    onClick={() => fileInputRef.current.click()}
                                >
                                    <Typography variant="body1" color="textSecondary">No resume uploaded</Typography>
                                    <Button
                                        variant="contained"
                                        sx={{ mt: 2, backgroundColor: "#1575C6", "&:hover": { backgroundColor: "#0056b3" } }}
                                    >
                                        Please upload in PDF (preferred) or word format
                                    </Button>
                                </Box>
                            )}
                        </Box>

                    </DialogContent>
                </Dialog>
            )
            }
        </OnboardingContext.Provider >
    );
};

export const useOnboarding = () => useContext(OnboardingContext);